import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  formatDateRange,
  localDateTimeInputToApi,
  parseDateInput,
  toLocalDateTimeInput,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../../components';

export const AvailableDateFilter = (): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [searchParams, setSearchParams] = useSearchParams();
  const start = searchParams.get('start') ?? '';
  const end = searchParams.get('end') ?? '';

  const [editing, setEditing] = useState<boolean>(!start && !end);
  const [stagedStart, setStagedStart] = useState<string>(toLocalDateTimeInput(start));
  const [stagedEnd, setStagedEnd] = useState<string>(toLocalDateTimeInput(end));

  useEffect(() => {
    setStagedStart(toLocalDateTimeInput(start));
    setStagedEnd(toLocalDateTimeInput(end));
    setEditing(!start && !end);
  }, [start, end]);

  const error = useMemo((): string => {
    if (!stagedStart || !stagedEnd) return '';
    const startDate = parseDateInput(stagedStart);
    const endDate = parseDateInput(stagedEnd);
    return endDate.getTime() > startDate.getTime()
      ? ''
      : t('pages.sitters.availableDate.endAfterStart');
  }, [stagedStart, stagedEnd, t]);

  const apply = (): void => {
    if (error || !stagedStart || !stagedEnd) return;
    const apiStart = localDateTimeInputToApi(stagedStart);
    const apiEnd = localDateTimeInputToApi(stagedEnd);
    if (!apiStart || !apiEnd) return;
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.set('start', apiStart);
        next.set('end', apiEnd);
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    setEditing(false);
  };

  const clear = (): void => {
    setStagedStart('');
    setStagedEnd('');
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.delete('start');
        next.delete('end');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    setEditing(true);
  };

  if (!editing && start && end) {
    return (
      <div className='mb-3 text-sm'>
        <span className='font-semibold mr-2'>{t('pages.sitters.availableDate.label')}</span>
        <span className='mr-3'>{formatDateRange(start, end)}</span>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={(): void => setEditing(true)}
          className='underline'
        >
          {t('pages.sitters.availableDate.change')}
        </Button>
        <span className='mx-1'>|</span>
        <Button
          buttonType={ButtonType.tertiary}
          onClick={clear}
          className='underline'
        >
          {t('pages.sitters.availableDate.clear')}
        </Button>
      </div>
    );
  }

  return (
    <div className='mb-3'>
      <div className='font-semibold mb-2 text-sm'>
        {t('pages.sitters.availableDate.label')}
      </div>
      <div className='flex flex-wrap items-end gap-3'>
        <div>
          <label htmlFor='available-start' className='block text-xs mb-1'>
            {t('pages.sitters.availableDate.startingLabel')}
          </label>
          <input
            id='available-start'
            type='datetime-local'
            className='form-control border border-neutral-300 rounded px-2 py-1 text-sm'
            value={stagedStart}
            onChange={(e): void => setStagedStart(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor='available-end' className='block text-xs mb-1'>
            {t('pages.sitters.availableDate.endingLabel')}
          </label>
          <input
            id='available-end'
            type='datetime-local'
            className='form-control border border-neutral-300 rounded px-2 py-1 text-sm'
            value={stagedEnd}
            onChange={(e): void => setStagedEnd(e.target.value)}
          />
        </div>
        <Button
          buttonType={ButtonType.primary}
          disabled={!stagedStart || !stagedEnd || !!error}
          onClick={apply}
        >
          {t('common.apply')}
        </Button>
        {(start || end || stagedStart || stagedEnd) && (
          <Button buttonType={ButtonType.secondary} onClick={clear}>
            {t('pages.sitters.availableDate.clear')}
          </Button>
        )}
      </div>
      {error && (
        <div role='alert' className='text-error-500 text-sm mt-1'>
          {error}
        </div>
      )}
    </div>
  );
};
